// ---------------------------------------------------------------------------
// Single source of truth for platform-wide constants.
// Import from here — never re-declare these lists in components.
// The BRAND WORDMARK itself lives in src/lib/brand.ts (GIGGONE).
// ---------------------------------------------------------------------------
import { BRAND_NAME, BRAND_SUFFIX, BRAND_FULL_NAME } from '@/lib/brand';


/**
 * VERIFIED BUSINESS ADDRESS — single source of truth.
 *
 * This is the registered mailing address for GIGGONE Marketplace. It is shown
 * to humans (footer, Contact, About) AND emitted as machine-readable
 * schema.org PostalAddress on every page (Organization + LocalBusiness), so
 * search engines, AI crawlers, security reputation vendors and enterprise IT
 * reviewers all resolve the same physical entity.
 *
 * Never retype the address anywhere — import BUSINESS_ADDRESS.
 */

export const BUSINESS_ADDRESS = {
  streetAddress: '15891 Foothill Blvd, Suite 5',
  addressLocality: 'Fontana',
  addressRegion: 'CA',
  postalCode: '92335',
  addressCountry: 'US',
  countryName: 'United States',
};

/** Address as display lines, e.g. for a stacked <address> block. */
export const ADDRESS_LINES: string[] = [
  BUSINESS_ADDRESS.streetAddress,
  `${BUSINESS_ADDRESS.addressLocality}, ${BUSINESS_ADDRESS.addressRegion} ${BUSINESS_ADDRESS.postalCode}`,
  BUSINESS_ADDRESS.countryName,
];

/** Address on one line, e.g. "15891 Foothill Blvd, Suite 5, Fontana, CA 92335, United States". */
export const ADDRESS_ONE_LINE = ADDRESS_LINES.join(', ');

/** schema.org PostalAddress node — used by Organization and LocalBusiness. */
export const postalAddressSchema = () => ({
  '@type': 'PostalAddress',
  streetAddress: BUSINESS_ADDRESS.streetAddress,
  addressLocality: BUSINESS_ADDRESS.addressLocality,
  addressRegion: BUSINESS_ADDRESS.addressRegion,
  postalCode: BUSINESS_ADDRESS.postalCode,
  addressCountry: BUSINESS_ADDRESS.addressCountry,
});

/**
 * BRAND — the wordmark itself lives in src/lib/brand.ts so that a single edit
 * restyles every surface (logo, header, footer, meta titles, Open Graph,
 * JSON-LD, email sender names, legal copy). Never type the brand by hand here.
 */
export const BRAND = {
  name: BRAND_NAME,
  suffix: BRAND_SUFFIX,
  fullName: BRAND_FULL_NAME,
  tagline_en: 'Where construction work finds the right crew.',
  tagline_es: 'Donde el trabajo de construcción encuentra al equipo correcto.',
  /** The ONLY public contact channel. No public support phone number is displayed. */
  email: 'info@giggone.com',
  /** Verified street address on one line. Structured parts: BUSINESS_ADDRESS. */
  address: ADDRESS_ONE_LINE,
  /** Human-readable region served, used in copy where a full address does not fit. */
  region: 'Inland Empire, California',
  /**
   * Official one-line company description. Single source of truth — used in the
   * footer, the About page, the Organization schema and llms.txt. Do not retype
   * this sentence anywhere else; import brandDescription() instead.
   */
  description_en: `${BRAND_NAME} is a marketplace connecting homeowners, general contractors and subcontractors.`,
  description_es: `${BRAND_NAME} es un marketplace que conecta a dueños de casa, contratistas generales y subcontratistas.`,
};


/** Official company description in the active language. */
export const brandDescription = (lang: Lang): string =>
  lang === 'es' ? BRAND.description_es : BRAND.description_en;


/**
 * ───────────────────────────────────────────────────────────────────────────
 * BRAND IMAGES FOR SEARCH + SOCIAL — single source of truth.
 * ───────────────────────────────────────────────────────────────────────────
 * LOGO_IMAGE   square mark, emitted as Organization.logo (ImageObject) so
 *              Google/Bing can resolve a crawlable logo for the brand entity.
 *              Same file the PWA manifest already ships, so there is exactly
 *              one logo asset on the platform.
 * SOCIAL_IMAGE 16:9 share card used for og:image / twitter:image on every
 *              page that does not provide its own. Without it, link previews
 *              and `summary_large_image` cards render blank.
 *
 * Never inline either URL in a component or in index.html's runtime code —
 * import from here so a rebrand is a one-line change.
 */
export const LOGO_IMAGE =
  'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789922279410_1fbf836f.webp';

export const SOCIAL_IMAGE =
  'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1790055887715_891b24ea.jpg';

/** Rendered dimensions of SOCIAL_IMAGE, declared to crawlers via og:image:*. */
export const SOCIAL_IMAGE_SIZE = { width: 1344, height: 768 };



/**
 * ───────────────────────────────────────────────────────────────────────────
 * WHATSAPP SALES / SUPPORT LINE — single source of truth.
 * ───────────────────────────────────────────────────────────────────────────
 * Used by the sitewide floating WhatsApp button (and anywhere else a "message
 * us" affordance is offered). Never retype the number: import SUPPORT_WHATSAPP
 * or call whatsappHref() so the digits, the display format and the prefilled
 * greeting can only ever change in one place.
 *
 * `digits` is the E.164 number WITHOUT the leading "+" — that is exactly the
 * format wa.me expects (https://wa.me/19097296207). `display` is the human
 * format shown in copy and in the aria-label.
 */
export const SUPPORT_WHATSAPP = {
  digits: '19097296207',
  display: '+1 (909) 729-6207',
  /** Prefilled first message, so the conversation opens with real context. */
  greeting_en:
    'Hi, I have a question about GIGGONE memberships, leads, or contractor services.',
  greeting_es:
    'Hola, tengo una pregunta sobre las membresías, los leads o los servicios para contratistas de GIGGONE.',

};

/**
 * Build the wa.me deep link with the prefilled greeting in the active language.
 * wa.me opens the native app on mobile and WhatsApp Web on desktop, so one URL
 * covers every device — no user-agent sniffing required.
 */
export const whatsappHref = (lang: Lang): string =>
  `https://wa.me/${SUPPORT_WHATSAPP.digits}?text=${encodeURIComponent(
    lang === 'es' ? SUPPORT_WHATSAPP.greeting_es : SUPPORT_WHATSAPP.greeting_en
  )}`;



/**
 * PUBLIC TRUST PAGES — Privacy Policy, Terms of Service, Contact Us, About Us.
 * These four pages must be reachable with NO authentication, must be linked in
 * the footer of every page, and must stay crawlable (see public/robots.txt).
 * Single source of truth: the footer, the About page and the sitemap all read
 * this list, so a page can never be linked in one place and missed in another.
 */
export interface TrustPage {
  /** English route path (path() localizes it automatically). */
  to: string;
  /** translation key for the link label */
  key: string;
}

export const TRUST_PAGES: TrustPage[] = [
  { to: '/about', key: 'footer.aboutUs' },
  { to: '/contact', key: 'footer.contactUs' },
  { to: '/privacy', key: 'footer.privacy' },
  { to: '/terms', key: 'footer.terms' },
];



/**
 * FIXED LEAD PRICE — one price for every lead on the marketplace.
 * A lead never costs more or less because of the trade, the project type,
 * the budget, the city or who posted it. $29.00 = 2900 cents.
 * Import LEAD_PRICE anywhere a lead price is shown or charged.
 */
export const LEAD_PRICE = 2900;

/**
 * Subscription plans. Prices/allowances are mirrored from mkt_plans and are the
 * values the hosted checkout links and the payment webhook must agree on.
 *   Basic   $99/mo   ->  5 leads   (monthly, cancel anytime)
 *   Pro     $199/mo  -> 10 leads   (monthly, cancel anytime)
 *   Premium $499/mo  -> 25 leads   (monthly charge, 12-MONTH COMMITMENT)
 *
 * PREMIUM ALLOWANCE CHANGE (25, previously 20): the monthly charge is
 * UNCHANGED at $499. Only the included credit count moved. The same number
 * lives in exactly three places and they must never disagree:
 *   1. mkt_plans.included_leads  (database — what Pricing renders)
 *   2. PLAN_INCLUDED_LEADS below (client display + schema/FAQ copy)
 *   3. PLAN_LEADS in the payment-webhook / checkout-link edge functions
 *      (what a purchase and a renewal actually grant)
 *
 * PREMIUM BILLING — read before changing anything here:
 * PLAN_PRICES.premium is the MONTHLY charge ($499), not a term total. The buyer
 * pays $499 each month for 12 months; they are NOT charged $5,988 upfront.
 * PLAN_TERM_MONTHS.premium = 12 is the length of the COMMITMENT, not the
 * billing interval — every plan bills monthly. The $5,988 total agreement value
 * is always derived with planTermTotal(), never retyped, so the two numbers can
 * never drift apart. Always render a plan price through planBillingSuffix() /
 * planPriceLabel() and pair a committed plan with planTermNote().
 */
export const PLAN_CODES = ['basic', 'pro', 'premium'] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

export const PLAN_INCLUDED_LEADS: Record<string, number> = {
  basic: 5,
  pro: 10,
  /** Raised from 20 to 25. The $499 monthly charge did not change. */
  premium: 25,
};

/** MONTHLY charge for each plan, in cents. Premium is $499/mo — not a term total. */
export const PLAN_PRICES: Record<string, number> = {
  basic: 9900,
  pro: 19900,
  /** $499 charged every month; requires a 12-month commitment (see PLAN_TERM_MONTHS). */
  premium: 49900,
};

/**
 * ONE-OFF PRODUCT — the $29 single lead credit.
 *
 * Not a plan: it never renews and never creates a subscription. It adds ONE
 * spendable credit to the balance, exactly like a membership credit, and the
 * contractor chooses which open project to spend it on.
 *
 * `code` is the value every purchase surface must send to the checkout-link
 * edge function, which resolves it to GHL_SINGLE_CHECKOUT_URL.
 */
export const SINGLE_LEAD_PRODUCT = {
  code: 'single' as const,
  price: LEAD_PRICE,
  credits: 1,
  recurring: false,
  /** Environment variable holding the hosted checkout URL for this product. */
  envName: 'GHL_SINGLE_CHECKOUT_URL',
};


/**
 * Minimum COMMITMENT length in months. This is not the billing interval —
 * every plan is charged monthly. 1 means month-to-month / cancel anytime.
 */
export const PLAN_TERM_MONTHS: Record<string, number> = {
  basic: 1,
  pro: 1,
  premium: 12,
};

export const planTermMonths = (code?: string | null): number =>
  (code && PLAN_TERM_MONTHS[code]) || 1;

/** Total value of a committed plan over its full term, in cents (e.g. $499 x 12 = $5,988). */
export const planTermTotal = (code: string | null | undefined, monthlyCents: number): number =>
  monthlyCents * planTermMonths(code);

/** Billing interval suffix. Every plan bills monthly, so this is always "/mo". */
export const planBillingSuffix = (_code: string | null | undefined, lang: Lang): string =>
  lang === 'es' ? '/mes' : '/mo';

/** Full price label, e.g. "$199/mo" or "$499/mo". */
export const planPriceLabel = (cents: number, code: string | null | undefined, lang: Lang): string =>
  `${money(cents)}${planBillingSuffix(code, lang)}`;
/**
 * COMMITMENT DISCLOSURE for any plan with a minimum term.
 *
 * PREMIUM CONTRADICTION FIX: Premium may NEVER be described as "cancel
 * anytime". The exact, approved disclosure is generated here (derived from
 * PLAN_PRICES + PLAN_TERM_MONTHS, never retyped) and is rendered by the plan
 * picker, the checkout modal and the pricing cards, so the monthly charge, the
 * $5,988 total agreement value, the "not charged upfront" clarification and
 * the credit-expiry rule can only ever exist in ONE place.
 *
 * Month-to-month plans (Basic, Pro) return an empty string — only they are
 * allowed to show `cta.cancelAnytime`, and planIsCancelAnytime() below is the
 * single guard every surface must use before rendering that phrase.
 */
export const planTermNote = (code: string | null | undefined, cents: number, lang: Lang): string => {
  const months = planTermMonths(code);
  if (months <= 1) return '';
  const total = money(planTermTotal(code, cents));
  return lang === 'es'
    ? `${money(cents)} al mes durante ${months} pagos mensuales. Valor total del acuerdo: ${total}. No se cobra el monto completo por adelantado. Los créditos de lead no usados expiran al final de cada ciclo de facturación y no se acumulan.`
    : `${money(cents)} per month for ${months} monthly payments. Total agreement value: ${total}. The full amount is not charged upfront. Unused lead credits expire at the end of each billing cycle and do not roll over.`;
};

/**
 * TRUE only for genuinely month-to-month plans. Every surface that wants to
 * print "cancel anytime" MUST gate on this, so a committed plan (Premium)
 * cannot contradict its own 12-month agreement anywhere on the site.
 */
export const planIsCancelAnytime = (code: string | null | undefined): boolean =>
  planTermMonths(code) <= 1;


/**
 * Static plan display names. The authoritative plan rows live in `mkt_plans`
 * (and the Pricing page renders those), but lightweight surfaces such as the
 * floating CTA plan picker need a label without waiting on a database round
 * trip. Prices/allowances are still read from PLAN_PRICES /
 * PLAN_INCLUDED_LEADS above so there is only ever one copy of those numbers.
 */
export const PLAN_NAMES: Record<string, { en: string; es: string }> = {
  basic: { en: 'Basic', es: 'Básico' },
  pro: { en: 'Pro', es: 'Pro' },
  premium: { en: 'Premium', es: 'Premium' },
};

export const planLabel = (code: string, lang: Lang): string =>
  (PLAN_NAMES[code] && PLAN_NAMES[code][lang]) || code;

export interface PlanOption {
  code: PlanCode;
  name: string;
  /** Monthly charge in cents. */
  price: number;
  /** Lead credits granted at the start of every billing cycle. */
  leads: number;
  /** Minimum commitment in months (1 = cancel anytime). */
  termMonths: number;
}

/**
 * The three membership plans as a ready-to-render list, DERIVED from the maps
 * above — never a second hand-typed copy of the pricing. Used by the floating
 * "Start Now" plan picker.
 */
export const planOptions = (lang: Lang): PlanOption[] =>
  PLAN_CODES.map((code) => ({
    code,
    name: planLabel(code, lang),
    price: PLAN_PRICES[code],
    leads: PLAN_INCLUDED_LEADS[code],
    termMonths: planTermMonths(code),
  }));


/**
 * ───────────────────────────────────────────────────────────────────────────
 * PLAN ALLOWANCE RECONCILIATION — stops "20 vs 25" drift from EVER shipping.
 * ───────────────────────────────────────────────────────────────────────────
 * The plan rows in `mkt_plans` carry two things that can disagree:
 *   included_leads  -> a NUMBER (authoritative-ish, editable in the database)
 *   features_en/es  -> FREE TEXT bullets that may also state a lead count
 *
 * Free text cannot be trusted: the Premium bullet said "20 included leads"
 * months after the allowance was raised to 25, so the Pricing page advertised
 * one number while the webhook granted another.
 *
 * From here on, PLAN_INCLUDED_LEADS (this file) is the ONE authority for what
 * a customer is told, and every customer-facing surface pipes plan copy
 * through `reconcileFeatureLine()`. Any bullet that states a lead count is
 * rewritten to the authoritative number at render time, so even if a stale row
 * is restored from a backup, or an admin retypes a bullet by hand, the site
 * physically cannot display a count that differs from what is granted.
 */

/** Authoritative monthly credits for a plan code (0 for unknown codes). */
export const planLeads = (code?: string | null): number =>
  (code && PLAN_INCLUDED_LEADS[code]) || 0;

/**
 * Matches any "<N> included leads ..." / "<N> leads incluidos ..." bullet in
 * either language, capturing the number so it can be replaced.
 */
const LEAD_COUNT_BULLET = /^(\s*)(\d+)(\s+)(included\s+leads|leads\s+incluidos)/i;

/**
 * Rewrite a single stored feature bullet so any stated lead count matches the
 * authoritative allowance. Bullets that do not mention a lead count are
 * returned untouched.
 */
export const reconcileFeatureLine = (code: string | null | undefined, line: string): string => {
  const n = planLeads(code);
  if (!n) return line;
  return line.replace(LEAD_COUNT_BULLET, (_m, pre, _old, gap, label) => `${pre}${n}${gap}${label}`);
};

/**
 * Customer-facing feature bullets for a plan, reconciled against
 * PLAN_INCLUDED_LEADS. Always use this instead of rendering features_en /
 * features_es straight out of the database.
 */
export const planFeatureLines = (
  code: string | null | undefined,
  features: string[] | null | undefined
): string[] => (features || []).map((f) => reconcileFeatureLine(code, f));

/**
 * Drift detector for the admin console: returns the bullets whose stated lead
 * count does not match the authoritative allowance, plus the mismatch between
 * a database `included_leads` value and PLAN_INCLUDED_LEADS. An empty result
 * means the stored copy and the granted credits agree exactly.
 */
export const planCopyDrift = (
  code: string,
  dbIncludedLeads: number | null | undefined,
  features: string[] | null | undefined
): string[] => {
  const n = planLeads(code);
  const out: string[] = [];
  if (n && dbIncludedLeads != null && dbIncludedLeads !== n) {
    out.push(`included_leads = ${dbIncludedLeads}, expected ${n}`);
  }
  (features || []).forEach((f) => {
    const m = f.match(LEAD_COUNT_BULLET);
    if (m && Number(m[2]) !== n) out.push(`"${f}" states ${m[2]}, expected ${n}`);
  });
  return out;
};




export type UserType = 'homeowner' | 'gc' | 'subcontractor';

export type Lang = 'en' | 'es';

/** Trade / category slugs shared by projects, work orders, filters and SEO URLs. */
export interface Trade {
  slug: string;
  en: string;
  es: string;
  /** es slug used in Spanish SEO urls */
  esSlug: string;
  homeowner: boolean;
  gc: boolean;
}

export const TRADES: Trade[] = [
  { slug: 'roofing', en: 'Roofing', es: 'Techado', esSlug: 'techado', homeowner: true, gc: true },
  { slug: 'plumbing', en: 'Plumbing', es: 'Plomería', esSlug: 'plomeria', homeowner: true, gc: true },
  { slug: 'electrical', en: 'Electrical', es: 'Electricidad', esSlug: 'electricidad', homeowner: true, gc: true },
  { slug: 'hvac', en: 'HVAC', es: 'Aire y Calefacción', esSlug: 'aire-acondicionado', homeowner: true, gc: true },
  { slug: 'flooring', en: 'Flooring', es: 'Pisos', esSlug: 'pisos', homeowner: true, gc: true },
  { slug: 'painting', en: 'Painting', es: 'Pintura', esSlug: 'pintura', homeowner: true, gc: true },
  { slug: 'solar', en: 'Solar', es: 'Solar', esSlug: 'solar', homeowner: true, gc: false },
  { slug: 'concrete', en: 'Concrete', es: 'Concreto', esSlug: 'concreto', homeowner: true, gc: true },
  { slug: 'landscaping', en: 'Landscaping', es: 'Jardinería', esSlug: 'jardineria', homeowner: true, gc: false },
  { slug: 'windows', en: 'Windows', es: 'Ventanas', esSlug: 'ventanas', homeowner: true, gc: false },
  { slug: 'doors', en: 'Doors', es: 'Puertas', esSlug: 'puertas', homeowner: true, gc: false },
  { slug: 'kitchen-remodel', en: 'Kitchen Remodel', es: 'Remodelación de Cocina', esSlug: 'remodelacion-cocina', homeowner: true, gc: false },
  { slug: 'bathroom-remodel', en: 'Bathroom Remodel', es: 'Remodelación de Baño', esSlug: 'remodelacion-bano', homeowner: true, gc: false },
  { slug: 'general-construction', en: 'General Construction', es: 'Construcción General', esSlug: 'construccion-general', homeowner: true, gc: true },
  { slug: 'framing', en: 'Framing', es: 'Estructura / Framing', esSlug: 'estructura', homeowner: false, gc: true },
  { slug: 'drywall', en: 'Drywall', es: 'Tablaroca', esSlug: 'tablaroca', homeowner: false, gc: true },
];

/** Spanish localized job slug: roofing-fontana-ca-1001 -> techado-fontana-ca-1001 */
export const localizedSlug = (slug: string, trade: string, lang: Lang): string => {
  if (lang !== 'es') return slug;
  const tr = TRADES.find((x) => x.slug === trade);
  if (!tr) return slug;
  return slug.startsWith(`${tr.slug}-`) ? `${tr.esSlug}-${slug.slice(tr.slug.length + 1)}` : slug;
};

/** Map a localized slug back to the canonical English slug stored in the database. */
export const canonicalSlug = (slug: string): string => {
  const tr = TRADES.find((x) => slug.startsWith(`${x.esSlug}-`));
  if (!tr) return slug;
  return `${tr.slug}-${slug.slice(tr.esSlug.length + 1)}`;
};

export const tradeLabel = (slug: string, lang: Lang): string => {

  const t = TRADES.find((x) => x.slug === slug);
  if (!t) return slug;
  return lang === 'es' ? t.es : t.en;
};

export const homeownerCategories = () => TRADES.filter((t) => t.homeowner);
export const gcTrades = () => TRADES.filter((t) => t.gc);

/**
 * ───────────────────────────────────────────────────────────────────────────
 * BUDGET BANDS — the single source of truth for budget display AND filtering.
 * ───────────────────────────────────────────────────────────────────────────
 * Every band carries BOTH the human label (EN/ES, stored in
 * mkt_listings.budget_range for display) and the STRUCTURED numbers written to
 * mkt_listings.budget_min / budget_max, with budget_currency = 'USD'.
 *
 * Why structured columns: filtering and sorting must never be derived from
 * display text. The old "$5,000 - $15,000" -> digits parse produced 500001 and
 * made the minimum-budget filter a no-op. Numbers are now saved at intake and
 * the free-text parse is only a legacy fallback for rows posted before this
 * change (see listingBudgetCeiling in src/lib/marketplace.ts).
 *
 * `max: null` = open-ended top band ("$100,000+").
 * "Not sure" is represented by the EMPTY selection: no label, no numbers, and
 * NO invented amount. A listing with no budget on file is never filtered out.
 */
export interface BudgetBand {
  /** English label stored in budget_range (canonical, used by SEO + filters). */
  en: string;
  es: string;
  /** Inclusive floor in whole dollars. */
  min: number;
  /** Inclusive ceiling in whole dollars, or null for an open-ended band. */
  max: number | null;
}

export const BUDGET_BANDS: BudgetBand[] = [
  { en: 'Under $5,000', es: 'Menos de $5,000', min: 0, max: 5000 },
  { en: '$5,000 - $15,000', es: '$5,000 - $15,000', min: 5000, max: 15000 },
  { en: '$15,000 - $30,000', es: '$15,000 - $30,000', min: 15000, max: 30000 },
  { en: '$30,000 - $60,000', es: '$30,000 - $60,000', min: 30000, max: 60000 },
  { en: '$60,000 - $100,000', es: '$60,000 - $100,000', min: 60000, max: 100000 },
  { en: '$100,000+', es: 'Más de $100,000', min: 100000, max: null },
];

/** Default currency for the structured budget columns. */
export const BUDGET_CURRENCY = 'USD';

/** Find a band from either its English or Spanish label. */
export const budgetBandFor = (label?: string | null): BudgetBand | null => {
  const raw = (label || '').trim();
  if (!raw) return null;
  return BUDGET_BANDS.find((b) => b.en === raw || b.es === raw) || null;
};

/**
 * Structured columns for a chosen label. Returns nulls for "Not sure" /
 * no selection so the database keeps an honest "unknown", never a guess.
 */
export const budgetColumnsFor = (label?: string | null) => {
  const band = budgetBandFor(label);
  return {
    budget_range: band ? band.en : null,
    budget_min: band ? band.min : null,
    budget_max: band ? band.max : null,
    budget_currency: BUDGET_CURRENCY,
  };
};

/** Guard used by the posting form: min must never exceed max. */
export const budgetRangeIsValid = (min: number | null, max: number | null): boolean => {
  if (min === null || max === null) return true; // open-ended / unknown is fine
  return min <= max;
};

/** Labels in the active language (posting form select). */
export const budgetLabels = (lang: Lang): string[] =>
  BUDGET_BANDS.map((b) => (lang === 'es' ? b.es : b.en));

/** Back-compat exports — derived, never a second hand-typed copy. */
export const BUDGET_RANGES = BUDGET_BANDS.map((b) => b.en);
export const BUDGET_RANGES_ES = BUDGET_BANDS.map((b) => b.es);


export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

/**
 * ───────────────────────────────────────────────────────────────────────────
 * COVER IMAGE LIBRARY — one entry for EVERY trade in TRADES.
 * ───────────────────────────────────────────────────────────────────────────
 * TRADE_IMAGES is the single source of truth for
 *   a) the trade photos on the home page trade strip (FEATURED_TRADE_SLUGS), and
 *   b) the trade-specific marketplace cover used when a project has no
 *      uploaded photo (see src/lib/coverImage.ts).
 *
 * RULE: every slug in TRADES must have a key here. A project must NEVER fall
 * through to a blank/gray tile, so `assertTradeImageCoverage()` below is used
 * by tradeCoverImage() to keep this honest if a trade is added later.
 *
 * All files are pre-compressed WebP (~40-290 KB) at 1200px wide so a card grid
 * stays cheap on mobile; they are served from the CDN with long cache headers.
 */
export const TRADE_IMAGES: Record<string, string> = {
  // ── featured six (also used by the home page trade strip) ──────────────
  roofing: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932679_5ad94eef.webp',
  plumbing: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932529_b0a6aff8.webp',
  electrical: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932540_9ea9a6d5.webp',
  hvac: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932578_c5d0c0ca.webp',
  concrete: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932739_4d717c52.webp',
  'kitchen-remodel': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601932682_cd1dbcd1.webp',
  // ── remaining marketplace trades ───────────────────────────────────────
  painting: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405216_602aac3b.webp',
  flooring: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405107_b8a230cb.webp',
  'bathroom-remodel': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405019_dd60fb7f.webp',
  landscaping: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405145_b0521d6d.webp',
  solar: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405090_8235e80e.webp',
  windows: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601405332_53f039ae.webp',
  doors: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404854_2f284fd0.webp',
  'general-construction': 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404899_5e69a5f9.webp',
  framing: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404835_9998792f.webp',
  drywall: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789601404806_5372cbae.webp',
};


/**
 * Cover-image aliases for SEO trade slugs and legacy/synonym categories that
 * are not themselves marketplace TRADES (see src/data/seo/trades.ts, which maps
 * ~50 SEO trades onto the 16 marketplace trades). Keeps a hub page or an
 * imported record from ever rendering a blank cover.
 */
export const TRADE_IMAGE_ALIASES: Record<string, string> = {
  'windows-doors': 'windows',
  window: 'windows',
  door: 'doors',
  paint: 'painting',
  floors: 'flooring',
  tile: 'flooring',
  roof: 'roofing',
  're-roof': 'roofing',
  hvac_install: 'hvac',
  'air-conditioning': 'hvac',
  kitchen: 'kitchen-remodel',
  bathroom: 'bathroom-remodel',
  bath: 'bathroom-remodel',
  remodel: 'general-construction',
  construction: 'general-construction',
  'residential-construction': 'general-construction',
  'commercial-construction': 'general-construction',
  'general-contracting': 'general-construction',
  demolition: 'general-construction',
  excavation: 'concrete',
  masonry: 'concrete',
  stucco: 'painting',
  foundation: 'concrete',
  hardscape: 'concrete',
  // Outdoor scopes posted under landscaping / general construction today. Listed
  // so pool & fencing projects also get a real photo if those trades are added.
  pool: 'landscaping',
  fencing: 'landscaping',
  fence: 'landscaping',
  irrigation: 'landscaping',
  turf: 'landscaping',
  carpentry: 'framing',
  'rough-carpentry': 'framing',
  insulation: 'drywall',
  cabinets: 'kitchen-remodel',
  electrician: 'electrical',
  plumber: 'plumbing',
};

/**
 * Resolve the branded cover photo for a trade/category slug.
 * Order: exact trade -> alias -> null (caller falls back to the marketplace
 * cover). Never returns an empty string.
 */
export const tradeCoverImage = (slug?: string | null): string | null => {
  if (!slug) return null;
  const key = slug.trim().toLowerCase();
  const direct = TRADE_IMAGES[key];
  if (direct) return direct;
  const aliased = TRADE_IMAGE_ALIASES[key];
  return (aliased && TRADE_IMAGES[aliased]) || null;
};

/** Trades that have no cover photo yet — must always be empty in production. */
export const tradesMissingCoverImage = (): string[] =>
  TRADES.filter((tr) => !tradeCoverImage(tr.slug)).map((tr) => tr.slug);

/** The six trades featured in the "Every trade on the jobsite" section, in order. */
export const FEATURED_TRADE_SLUGS = [
  'roofing',
  'plumbing',
  'electrical',
  'hvac',
  'concrete',
  'kitchen-remodel',
];

export const IMAGES = {
  hero: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1788910228966_072120a3.jpg',
};


/**
 * HERO MOTION SEQUENCE — the cinematic cross-fading backdrop behind the home
 * page headline. Each frame is a real photo of the actual marketplace journey:
 * homeowner documenting a project, a crew working, the finished remodel, the
 * handshake at completion.
 *
 * WHY A PHOTO SEQUENCE AND NOT AN AUTOPLAY <video>:
 * an autoplaying background video costs 3-8 MB on first paint, is the single
 * biggest LCP regression available, is throttled or blocked outright on iOS
 * Low Power Mode and on metered mobile connections, and cannot be captioned.
 * These four WebP frames total ~390 KB, animate with GPU-only transforms, and
 * respect prefers-reduced-motion. If a produced brand film is ever licensed,
 * set HERO_VIDEO_URL below and HeroVideo renders it as the backdrop instead —
 * no other code has to change.
 */
export const HERO_SEQUENCE: { src: string; alt_en: string; alt_es: string }[] = [
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671647_1869f8a1.webp',
    alt_en: 'Homeowner photographing a damaged kitchen cabinet to post a project',
    alt_es: 'Dueña de casa fotografiando un gabinete dañado para publicar un proyecto',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671679_39e87fe1.webp',
    alt_en: 'Roofing crew installing shingles on a residential roof',
    alt_es: 'Cuadrilla de techado instalando tejas en un techo residencial',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671650_b62cadf2.webp',
    alt_en: 'Completed modern kitchen remodel with quartz island',
    alt_es: 'Remodelación de cocina moderna terminada con isla de cuarzo',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789514671696_721ec938.webp',
    alt_en: 'Homeowner and contractor shaking hands after a completed project',
    alt_es: 'Dueño de casa y contratista dándose la mano al terminar el proyecto',
  },
];

/**
 * Optional produced brand film. Leave as an empty string until a real MP4 is
 * licensed and hosted — HeroVideo falls back to HERO_SEQUENCE automatically.
 * Never point this at a third-party/hotlinked video: a cross-origin media URL
 * on the landing page is a reputation signal security vendors score against.
 */
export const HERO_VIDEO_URL = '';

/**
 * Trade strip accent colors. Keyed by trade slug so the horizontal category
 * rail, the trade hub pages and any future filter chips all tint identically.
 * Only trades listed here get a custom tint; the rest fall back to slate.
 */
export const TRADE_ACCENTS: Record<string, { bg: string; fg: string; ring: string }> = {
  roofing: { bg: 'bg-orange-50', fg: 'text-orange-600', ring: 'ring-orange-200' },
  plumbing: { bg: 'bg-sky-50', fg: 'text-sky-600', ring: 'ring-sky-200' },
  electrical: { bg: 'bg-amber-50', fg: 'text-amber-600', ring: 'ring-amber-200' },
  concrete: { bg: 'bg-slate-100', fg: 'text-slate-600', ring: 'ring-slate-300' },
  painting: { bg: 'bg-violet-50', fg: 'text-violet-600', ring: 'ring-violet-200' },
  flooring: { bg: 'bg-rose-50', fg: 'text-rose-600', ring: 'ring-rose-200' },
  hvac: { bg: 'bg-cyan-50', fg: 'text-cyan-600', ring: 'ring-cyan-200' },
  landscaping: { bg: 'bg-emerald-50', fg: 'text-emerald-600', ring: 'ring-emerald-200' },
  'kitchen-remodel': { bg: 'bg-indigo-50', fg: 'text-indigo-600', ring: 'ring-indigo-200' },
  'bathroom-remodel': { bg: 'bg-teal-50', fg: 'text-teal-600', ring: 'ring-teal-200' },
  solar: { bg: 'bg-yellow-50', fg: 'text-yellow-600', ring: 'ring-yellow-200' },
  windows: { bg: 'bg-blue-50', fg: 'text-blue-600', ring: 'ring-blue-200' },
  doors: { bg: 'bg-stone-100', fg: 'text-stone-600', ring: 'ring-stone-300' },
  framing: { bg: 'bg-lime-50', fg: 'text-lime-700', ring: 'ring-lime-200' },
  drywall: { bg: 'bg-neutral-100', fg: 'text-neutral-600', ring: 'ring-neutral-300' },
  'general-construction': { bg: 'bg-slate-100', fg: 'text-slate-700', ring: 'ring-slate-300' },
};

export const tradeAccent = (slug: string) =>
  TRADE_ACCENTS[slug] || { bg: 'bg-slate-100', fg: 'text-slate-600', ring: 'ring-slate-300' };

/**
 * TYPICAL project duration by trade, in working days [min, max].
 *
 * These are EDITORIAL PLANNING ESTIMATES for a standard single-family job, not
 * a promise and not derived from platform data. Every surface that renders them
 * must label them as typical (see `jobs.typicalTimeline`) so nobody mistakes an
 * estimate for a committed schedule. Scope, permits and inspections move real
 * timelines, which is why no per-listing duration is ever invented.
 */
export const TYPICAL_DURATION_DAYS: Record<string, [number, number]> = {
  roofing: [2, 5],
  plumbing: [1, 3],
  electrical: [1, 4],
  hvac: [1, 3],
  flooring: [2, 6],
  painting: [2, 5],
  solar: [2, 4],
  concrete: [3, 7],
  landscaping: [3, 10],
  windows: [1, 3],
  doors: [1, 2],
  'kitchen-remodel': [15, 45],
  'bathroom-remodel': [10, 25],
  'general-construction': [10, 60],
  framing: [5, 20],
  drywall: [3, 8],
};

/** Typical duration label, e.g. "2-5 days". Returns null when unknown. */
export const typicalDurationLabel = (trade: string, lang: Lang): string | null => {
  const range = TYPICAL_DURATION_DAYS[trade];
  if (!range) return null;
  const [lo, hi] = range;
  return lang === 'es' ? `${lo}-${hi} días` : `${lo}-${hi} days`;
};



export const ADMIN_EMAIL = 'raulrodartejr@skyisthelimitshop.com';

/** Cents -> display string with thousands separators, e.g. 598800 -> "$5,988". */
export const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  })}`;


/** Add-on modules — intentionally kept OUT of primary navigation. */
export const ADDONS = [
  { code: 'crm', price: 4900 },
  { code: 'crew', price: 3900 },
  { code: 'chat', price: 2900 },
  { code: 'logs', price: 2900 },
];
